/**
 * Distance between two points, for sorting and labelling search results.
 *
 * <p>Straight-line ("as the crow flies"), not travel distance — we have coordinates, not a routing
 * service, and it matches how the backend's radius search works, so a 25 km search never returns
 * something the list then calls 31 km away.
 */

const EARTH_RADIUS_KM = 6371;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

/** Haversine. Accurate to well under a percent at the distances this app deals in. */
export function distanceKm(from: [number, number], to: [number, number]): number {
  const [lat1, lng1] = from;
  const [lat2, lng2] = to;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

/**
 * Parses an event's coordinates, which arrive as decimal strings from the JSON. Returns null when
 * either is missing — plenty of events have no location recorded.
 */
export function coordinatesOf(
  latitude: string | null,
  longitude: string | null,
): [number, number] | null {
  if (!latitude || !longitude) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
}

const MILES_PER_KM = 0.621371;
const YARDS_PER_MILE = 1760;

/**
 * A distance as a British reader expects to see it: "400 yd", "3.2 miles", "24 miles".
 *
 * <p>Miles at the surface, kilometres underneath. Everything that calculates — {@link distanceKm},
 * the radius sent to the backend, the map's own maths — stays metric, because the database query
 * takes metres and converting in two places is how a 25-mile search starts returning something the
 * list calls 26 miles away. One conversion, at the last possible moment, is this function.
 *
 * <p>Precision drops as the number grows: nobody plans around the difference between 24 and 24.3
 * miles, and a decimal there only makes the column harder to scan.
 */
export function formatDistance(km: number): string {
  const miles = km * MILES_PER_KM;
  // Under a quarter mile, "0.2 miles" is a worse answer than the walk it describes.
  if (miles < 0.25) return `${Math.round(miles * YARDS_PER_MILE)} yd`;
  if (miles < 10) return `${miles.toFixed(1)} miles`;
  return `${Math.round(miles)} miles`;
}
